import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  api,
  OnboardingProgress,
  Permission,
  useAuthBootstrap,
  useAuthStore,
  usePermission,
  usePermissions,
  useThemeStore,
} from '@lipaflow/shared'
import { LockClosedIcon } from '@heroicons/react/24/outline'

// Layouts
import DashboardLayout from './components/layouts/DashboardLayout'
import OnboardingLayout from './components/layouts/OnboardingLayout'
import { OnboardingErrorBoundary } from './components/ErrorBoundary'
import {
  isAdminAssistedSetupPending,
  shouldUseSelfServiceOnboarding,
} from './lib/onboardingRouting'

// Pages – lazy loaded for code-splitting
const LoginPage = lazy(() => import('./pages/LoginPage'))
const InvitePage = lazy(() => import('./pages/InvitePage'))
const SignupPage = lazy(() => import('./pages/SignupPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ClientsPage = lazy(() => import('./pages/ClientsPage'))
const BeneficiariesPage = lazy(() => import('./pages/BeneficiariesPage'))
const ClaimsPage = lazy(() => import('./pages/ClaimsPage'))
const SessionsPage = lazy(() => import('./pages/SessionsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const BalanceUploadPage = lazy(() => import('./pages/BalanceUploadPage'))
const PaymentPlansPage = lazy(() => import('./pages/PaymentPlansPage'))
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'))
const PledgesPage = lazy(() => import('./pages/PledgesPage'))
const EscalationsPage = lazy(() => import('./pages/EscalationsPage'))
const SegmentsPage = lazy(() => import('./pages/SegmentsPage'))
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'))
const BroadcastsPage = lazy(() => import('./pages/BroadcastsPage'))
const ResultsPage = lazy(() => import('./pages/ResultsPage'))
const AttendancePage = lazy(() => import('./pages/AttendancePage'))
const PlanningPage = lazy(() => import('./pages/PlanningPage'))
const CurriculumBrowserPage = lazy(() => import('./pages/CurriculumBrowserPage'))
const GenerationHistoryPage = lazy(() => import('./pages/GenerationHistoryPage'))
const LeasesPage = lazy(() => import('./pages/LeasesPage'))
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'))
const AuditTrailPage = lazy(() => import('./pages/AuditTrailPage'))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const FeeStructuresPage = lazy(() => import('./pages/FeeStructuresPage'))
const InsightsPage = lazy(() => import('./pages/InsightsPage'))
const ClassroomDashboardPage = lazy(() => import('./pages/ClassroomDashboardPage'))
const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage'))
const TimetablePage = lazy(() => import('./pages/TimetablePage'))
const DisciplinePage = lazy(() => import('./pages/DisciplinePage'))
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'))
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'))
const KnecAlignmentPage = lazy(() => import('./pages/KnecAlignmentPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const PaymentReturnPage = lazy(() => import('./pages/PaymentReturnPage'))
const SupportSessionPage = lazy(() => import('./pages/SupportSessionPage'))

// Onboarding – dynamic step loader
import StepLoader from './pages/onboarding/StepLoader'

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { isValidating } = useAuthBootstrap()

  if (isValidating) {
    return <PageLoader />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PermissionRoute({
  children,
  permission,
  permissions,
}: {
  children: React.ReactNode
  permission?: Permission
  permissions?: Permission[]
}) {
  const hasSinglePermission = usePermission(permission as Permission)
  const hasAllPermissions = usePermissions(permissions || [])
  const isAllowed = permission ? hasSinglePermission : hasAllPermissions

  if (!isAllowed) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <LockClosedIcon className="w-10 h-10 text-warning-500 mb-3" />
        <h2 className="text-lg font-semibold text-stone-900">Access Restricted</h2>
        <p className="text-sm text-stone-500 mt-1">
          You do not have permission to view this page.
        </p>
      </div>
    )
  }

  return <>{children}</>
}

function OnboardingPendingScreen({
  adminAssisted = false,
}: {
  adminAssisted?: boolean
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cloud-100 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-card border border-stone-100 p-8 text-center animate-fade-in">
        <div className="w-16 h-16 mx-auto mb-4 bg-teal-50 rounded-full flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
        <h2 className="font-outfit text-h3 text-stone-800 mb-2">
          {adminAssisted ? 'Setup In Progress' : 'Setting Up Your School'}
        </h2>
        <p className="font-inter text-body text-stone-500">
          {adminAssisted
            ? 'The LipaFlow team is still completing this institution setup. You will be able to access the portal once setup is finished.'
            : 'Your administrator is still completing the initial setup. You will be able to access the portal once setup is finished.'}
        </p>
      </div>
    </div>
  )
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)

  const { data: progress, isLoading } = useQuery<OnboardingProgress>({
    queryKey: ['onboarding-progress-gate', user?.tenant_id],
    queryFn: async () => {
      const res = await api.get('/onboarding/progress')
      return res.data
    },
    enabled: isAuthenticated && !!user,
    retry: false,
  })

  if (isAuthenticated && user) {
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )
    }

    if (progress && !progress.is_complete) {
      if (isAdminAssistedSetupPending(progress)) {
        return <OnboardingPendingScreen adminAssisted />
      }
      if (shouldUseSelfServiceOnboarding(progress, user.role)) {
        return <Navigate to="/onboarding" replace />
      }
      return <OnboardingPendingScreen />
    }
  }

  return <>{children}</>
}

function OnboardingRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const { data: progress, isLoading } = useQuery<OnboardingProgress>({
    queryKey: ['onboarding-progress-route', user?.tenant_id],
    queryFn: async () => {
      const res = await api.get('/onboarding/progress')
      return res.data
    },
    enabled: isAuthenticated && !!user,
    retry: false,
  })

  // Check authentication
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  // Check if user is admin
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-warning-100 rounded-full flex items-center justify-center">
            <LockClosedIcon className="w-8 h-8 text-warning-600" />
          </div>

          <h2 className="text-2xl font-bold text-stone-900 mb-2">
            Admin Access Required
          </h2>

          <p className="text-stone-600 mb-6">
            Only institution administrators can access onboarding setup.
            Contact your admin to complete the setup process.
          </p>

          <div className="bg-info-50 border border-info-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-info-900 font-medium mb-2">
              Your current role: <span className="font-bold capitalize">{user.role}</span>
            </p>
            <p className="text-sm text-info-800">
              If you need admin access, contact your institution administrator
              or LipaFlow support at support@lipaflow.com
            </p>
          </div>

          <a
            href="/"
            className="inline-block px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return <PageLoader />
  }

  if (isAdminAssistedSetupPending(progress)) {
    return <OnboardingPendingScreen adminAssisted />
  }

  return (
    <OnboardingErrorBoundary>
      <OnboardingLayout />
    </OnboardingErrorBoundary>
  )
}

function App() {
  // Sync dark mode class on initial mount from persisted store
  const isDark = useThemeStore((s) => s.isDark)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
  }, [isDark])

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/support-session" element={<SupportSessionPage />} />

        {/* Onboarding Flow - Admin Only (dynamic step routing) */}
        <Route
          path="/onboarding"
          element={<OnboardingRoute />}
        >
          <Route index element={<Navigate to="step/set_institution_details" replace />} />
          <Route path="step/:stepId" element={<StepLoader />} />
        </Route>

        {/* Paystack same-tab callback — must stay reachable mid-onboarding,
            so it lives outside the OnboardingGuard */}
        <Route
          path="/payments/return"
          element={
            <ProtectedRoute>
              <PaymentReturnPage />
            </ProtectedRoute>
          }
        />

        {/* Main Dashboard */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout />
              </OnboardingGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="clients" element={<PermissionRoute permission="beneficiaries:view"><ClientsPage /></PermissionRoute>} />
          <Route path="entities" element={<PermissionRoute permission="beneficiaries:view"><BeneficiariesPage /></PermissionRoute>} />
          <Route path="results" element={<PermissionRoute permission="results:view"><ResultsPage /></PermissionRoute>} />
          <Route path="attendance" element={<PermissionRoute permission="attendance:view"><AttendancePage /></PermissionRoute>} />
          <Route path="classroom" element={<PermissionRoute permission="classes:view"><ClassroomDashboardPage /></PermissionRoute>} />
          <Route path="planning" element={<PermissionRoute permission="timetable:view"><PlanningPage /></PermissionRoute>} />
          <Route path="planning/history" element={<PermissionRoute permission="timetable:view"><GenerationHistoryPage /></PermissionRoute>} />
          <Route path="curriculum" element={<PermissionRoute permission="timetable:view"><CurriculumBrowserPage /></PermissionRoute>} />
          <Route path="assessment" element={<PermissionRoute permission="timetable:manage"><AssessmentPage /></PermissionRoute>} />
          <Route path="knec-alignment" element={<PermissionRoute permission="timetable:manage"><KnecAlignmentPage /></PermissionRoute>} />
          <Route path="assignments" element={<PermissionRoute permission="assignments:view"><AssignmentsPage /></PermissionRoute>} />
          <Route path="timetable" element={<PermissionRoute permission="timetable:view"><TimetablePage /></PermissionRoute>} />
          <Route path="discipline" element={<PermissionRoute permission="discipline:view"><DisciplinePage /></PermissionRoute>} />
          <Route path="announcements" element={<PermissionRoute permission="broadcasts:view"><AnnouncementsPage /></PermissionRoute>} />
          <Route path="claims" element={<PermissionRoute permission="claims:view"><ClaimsPage /></PermissionRoute>} />
          <Route path="subscriptions" element={<PermissionRoute permission="settings:view"><SubscriptionPage /></PermissionRoute>} />
          <Route path="balance-upload" element={<PermissionRoute permission="balance:upload"><BalanceUploadPage /></PermissionRoute>} />
          <Route path="payment-plans" element={<PermissionRoute permission="plans:view"><PaymentPlansPage /></PermissionRoute>} />
          <Route path="pledges" element={<PermissionRoute permission="pledges:view"><PledgesPage /></PermissionRoute>} />
          <Route path="escalations" element={<PermissionRoute permission="escalations:view"><EscalationsPage /></PermissionRoute>} />
          <Route path="segments" element={<PermissionRoute permission="segments:view"><SegmentsPage /></PermissionRoute>} />
          <Route path="conversations" element={<PermissionRoute permission="sessions:view"><SessionsPage /></PermissionRoute>} />
          <Route path="templates" element={<PermissionRoute permission="broadcasts:view"><TemplatesPage /></PermissionRoute>} />
          <Route path="broadcasts" element={<PermissionRoute permission="broadcasts:view"><BroadcastsPage /></PermissionRoute>} />
          <Route path="reports" element={<PermissionRoute permission="reports:view"><ReportsPage /></PermissionRoute>} />
          <Route path="insights" element={<PermissionRoute permission="reports:view"><InsightsPage /></PermissionRoute>} />
          <Route path="audit-trail" element={<PermissionRoute permission="audit:view"><AuditTrailPage /></PermissionRoute>} />
          <Route path="fee-structures" element={<PermissionRoute permission="fee_structures:view"><FeeStructuresPage /></PermissionRoute>} />
          <Route path="settings" element={<PermissionRoute permission="settings:view"><SettingsPage /></PermissionRoute>} />
          {/* Rental-specific routes (feature-gated at component level) */}
          <Route path="leases" element={<PermissionRoute permission="beneficiaries:view"><LeasesPage /></PermissionRoute>} />
          <Route path="maintenance" element={<PermissionRoute permission="beneficiaries:view"><MaintenancePage /></PermissionRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default App
